import {
    CalendarIcon,
    ChartBarIcon,
    ClockIcon,
    Cog6ToothIcon,
    PlusCircleIcon,
    UsersIcon,
    ArrowRightIcon,
    FireIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import apiService, { GymStats } from '../services/api';
import clsx from 'clsx';

interface RecentMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  is_active: boolean;
}

interface RecentBooking {
  id: number;
  class_name: string;
  member_name: string;
  date: string;
  start_time: string;
  booked_at: string;
}

interface UpcomingClass {
  id: number;
  class_name: string;
  date: string;
  start_time: string;
  end_time: string;
  max_capacity: number;
  current_bookings: number;
}

const DashboardPage: React.FC = () => {
  const { user, gym } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState<GymStats | null>(null);
  const [recentMembers, setRecentMembers] = useState<RecentMember[]>([]);
  const [recentBookings, setRecentBookings] = useState<RecentBooking[]>([]);
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  usePageTitle('Dashboard');

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [gymStats, dashboardData] = await Promise.all([
          apiService.getGymStats(),
          !user?.is_ceo ? apiService.getDashboardData().catch(() => ({
            recent_members: [],
            recent_bookings: [],
            upcoming_classes: []
          })) : Promise.resolve({
            recent_members: [],
            recent_bookings: [],
            upcoming_classes: []
          })
        ]);
        
        setStats(gymStats);
        if (!user?.is_ceo && dashboardData) {
          setRecentMembers(dashboardData.recent_members || []);
          setRecentBookings(dashboardData.recent_bookings || []);
          setUpcomingClasses(dashboardData.upcoming_classes || []);
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.is_ceo]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const statCards = [
    {
      name: 'Total Members',
      value: stats?.total_members || 0,
      icon: UsersIcon,
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      route: '/members',
    },
    {
      name: 'Active Workouts',
      value: stats?.active_workouts || 0,
      icon: ChartBarIcon,
      color: 'bg-gradient-to-r from-orange-500 to-amber-500',
      route: '/workouts',
    },
    {
      name: "Today's Classes",
      value: stats?.today_classes || 0,
      icon: CalendarIcon,
      color: 'bg-gradient-to-r from-orange-500 to-blue-500',
      route: '/classes',
    },
    {
      name: "Today's Bookings",
      value: stats?.today_bookings || 0,
      icon: ClockIcon,
      color: 'bg-gradient-to-r from-amber-500 to-orange-500',
      route: '/classes',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark' 
            ? "bg-gradient-to-r from-white to-gray-300" 
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          {user?.is_ceo ? 'Central Core+ CEO Dashboard' : gym ? `${gym.name} Dashboard` : 'Dashboard'}
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          {user?.is_ceo 
            ? `Welcome back, ${user?.first_name}! Here's the overview of the Central Core+ platform.`
            : `Welcome back, ${user?.first_name}! Here's what's happening with your gym today.`
          }
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <button
            key={stat.name}
            onClick={() => navigate(stat.route)}
            className={clsx(
              "group backdrop-blur-lg rounded-2xl shadow-2xl border p-6 transition-all duration-300 transform hover:scale-105 text-left w-full cursor-pointer",
              theme === 'dark'
                ? "bg-slate-800/60 border-slate-700/50 hover:bg-slate-800/80 hover:border-slate-600"
                : "bg-white/80 border-gray-200/50 hover:bg-white/90 hover:border-gray-300"
            )}
          >
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-3 rounded-xl ${stat.color} shadow-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className={clsx(
                  "text-sm font-medium transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>{stat.name}</p>
                <p className={clsx(
                  "text-3xl font-bold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>{stat.value}</p>
              </div>
              <ArrowRightIcon className={clsx(
                "h-5 w-5 transition-all duration-200 opacity-0 group-hover:opacity-100 group-hover:translate-x-1",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )} />
            </div>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className={clsx(
        "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
        theme === 'dark'
          ? "bg-slate-800/60 border-slate-700/50"
          : "bg-white/80 border-gray-200/50"
      )}>
        <h2 className={clsx(
          "text-2xl font-bold mb-6 text-center transition-colors",
          theme === 'dark' ? "text-white" : "text-gray-900"
        )}>Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {user?.is_ceo ? (
            // CEO Quick Actions
            <>
              <button 
                onClick={() => navigate('/gym-registration')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-blue-500 rounded-xl text-sm font-medium text-white hover:from-orange-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <PlusCircleIcon className="h-5 w-5 mr-2" />
                Register New Gym
              </button>
              <button 
                onClick={() => navigate('/gym-registration')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl text-sm font-medium text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                View All Gyms
              </button>
              <button 
                onClick={() => navigate('/reports')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-sm font-medium text-white hover:from-amber-600 hover:to-orange-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Platform Analytics
              </button>
              <button 
                onClick={() => navigate('/gym-settings')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-gray-600 to-slate-600 rounded-xl text-sm font-medium text-white hover:from-gray-700 hover:to-slate-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <Cog6ToothIcon className="h-5 w-5 mr-2" />
                System Settings
              </button>
            </>
          ) : (
            // Gym User Quick Actions
            <>
              <button 
                onClick={() => navigate('/members?action=create')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-blue-500 rounded-xl text-sm font-medium text-white hover:from-orange-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                Add Member
              </button>
              <button 
                onClick={() => navigate('/classes?action=create')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl text-sm font-medium text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <CalendarIcon className="h-5 w-5 mr-2" />
                Schedule Class
              </button>
              <button 
                onClick={() => navigate('/workouts?action=create')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-sm font-medium text-white hover:from-amber-600 hover:to-orange-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Create Workout
              </button>
              <button 
                onClick={() => navigate('/employee-management')}
                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-gray-600 to-slate-600 rounded-xl text-sm font-medium text-white hover:from-gray-700 hover:to-slate-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <UsersIcon className="h-5 w-5 mr-2" />
                Manage Staff
              </button>
            </>
          )}
        </div>
      </div>

      {/* Recent Activity & Upcoming Classes */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Members */}
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-6 transition-colors",
          theme === 'dark'
            ? "bg-slate-800/60 border-slate-700/50"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={clsx(
              "text-xl font-bold transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Recent Members</h2>
            <button
              onClick={() => navigate('/members')}
              className={clsx(
                "text-sm font-medium transition-colors hover:underline",
                theme === 'dark' ? "text-orange-400 hover:text-orange-300" : "text-orange-600 hover:text-orange-700"
              )}
            >
              View All
            </button>
          </div>
          {recentMembers.length > 0 ? (
            <div className="space-y-3">
              {recentMembers.slice(0, 5).map((member) => (
                <div
                  key={member.id}
                  onClick={() => navigate(`/members?member=${member.id}`)}
                  className={clsx(
                    "p-3 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.02]",
                    theme === 'dark'
                      ? "bg-white/5 hover:bg-white/10 border border-white/10"
                      : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={clsx(
                        "w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm",
                        "bg-gradient-to-r from-blue-500 to-cyan-500"
                      )}>
                        {member.first_name[0]}{member.last_name[0]}
                      </div>
                      <div>
                        <p className={clsx(
                          "font-medium transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {member.first_name} {member.last_name}
                        </p>
                        <p className={clsx(
                          "text-xs transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>
                          {formatDate(member.created_at)}
                        </p>
                      </div>
                    </div>
                    {member.is_active && (
                      <span className={clsx(
                        "px-2 py-1 text-xs font-semibold rounded-full",
                        theme === 'dark'
                          ? "bg-green-500/20 text-green-300 border border-green-500/30"
                          : "bg-green-100 text-green-700 border border-green-200"
                      )}>
                        Active
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={clsx(
              "text-center py-8 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-600"
            )}>
              <UsersIcon className={clsx(
                "h-12 w-12 mx-auto mb-3 transition-colors",
                theme === 'dark' ? "text-gray-500" : "text-gray-400"
              )} />
              <p>No recent members</p>
            </div>
          )}
        </div>

        {/* Recent Bookings */}
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-6 transition-colors",
          theme === 'dark'
            ? "bg-slate-800/60 border-slate-700/50"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={clsx(
              "text-xl font-bold transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Recent Bookings</h2>
            <button
              onClick={() => navigate('/classes')}
              className={clsx(
                "text-sm font-medium transition-colors hover:underline",
                theme === 'dark' ? "text-orange-400 hover:text-orange-300" : "text-orange-600 hover:text-orange-700"
              )}
            >
              View All
            </button>
          </div>
          {recentBookings.length > 0 ? (
            <div className="space-y-3">
              {recentBookings.slice(0, 5).map((booking) => (
                <div
                  key={booking.id}
                  onClick={() => navigate('/classes')}
                  className={clsx(
                    "p-3 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.02]",
                    theme === 'dark'
                      ? "bg-white/5 hover:bg-white/10 border border-white/10"
                      : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={clsx(
                        "font-medium transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>
                        {booking.class_name}
                      </p>
                      <p className={clsx(
                        "text-sm transition-colors mt-1",
                        theme === 'dark' ? "text-gray-400" : "text-gray-600"
                      )}>
                        {booking.member_name}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <CalendarIcon className={clsx(
                          "h-3 w-3 transition-colors",
                          theme === 'dark' ? "text-gray-500" : "text-gray-400"
                        )} />
                        <p className={clsx(
                          "text-xs transition-colors",
                          theme === 'dark' ? "text-gray-500" : "text-gray-500"
                        )}>
                          {formatDate(booking.date)} at {formatTime(booking.start_time)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={clsx(
              "text-center py-8 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-600"
            )}>
              <CalendarIcon className={clsx(
                "h-12 w-12 mx-auto mb-3 transition-colors",
                theme === 'dark' ? "text-gray-500" : "text-gray-400"
              )} />
              <p>No recent bookings</p>
            </div>
          )}
        </div>

        {/* Upcoming Classes Today */}
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-6 transition-colors",
          theme === 'dark'
            ? "bg-slate-800/60 border-slate-700/50"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={clsx(
              "text-xl font-bold transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>Upcoming Today</h2>
            <button
              onClick={() => navigate('/classes')}
              className={clsx(
                "text-sm font-medium transition-colors hover:underline",
                theme === 'dark' ? "text-orange-400 hover:text-orange-300" : "text-orange-600 hover:text-orange-700"
              )}
            >
              View All
            </button>
          </div>
          {upcomingClasses.length > 0 ? (
            <div className="space-y-3">
              {upcomingClasses.slice(0, 5).map((classItem) => {
                const capacityPercent = (classItem.current_bookings / classItem.max_capacity) * 100;
                return (
                  <div
                    key={classItem.id}
                    onClick={() => navigate('/classes')}
                    className={clsx(
                      "p-3 rounded-lg cursor-pointer transition-all duration-200 hover:scale-[1.02]",
                      theme === 'dark'
                        ? "bg-white/5 hover:bg-white/10 border border-white/10"
                        : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
                    )}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className={clsx(
                          "font-medium transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {classItem.class_name}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <ClockIcon className={clsx(
                            "h-3 w-3 transition-colors",
                            theme === 'dark' ? "text-gray-500" : "text-gray-400"
                          )} />
                          <p className={clsx(
                            "text-xs transition-colors",
                            theme === 'dark' ? "text-gray-500" : "text-gray-500"
                          )}>
                            {formatTime(classItem.start_time)} - {formatTime(classItem.end_time)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-600"
                        )}>
                          {classItem.current_bookings} / {classItem.max_capacity} booked
                        </span>
                        <span className={clsx(
                          "font-semibold transition-colors",
                          capacityPercent >= 90
                            ? "text-red-500"
                            : capacityPercent >= 70
                            ? "text-yellow-500"
                            : "text-green-500"
                        )}>
                          {Math.round(capacityPercent)}%
                        </span>
                      </div>
                      <div className={clsx(
                        "w-full h-2 rounded-full overflow-hidden",
                        theme === 'dark' ? "bg-gray-700" : "bg-gray-200"
                      )}>
                        <div
                          className={clsx(
                            "h-full transition-all duration-300",
                            capacityPercent >= 90
                              ? "bg-gradient-to-r from-red-500 to-red-600"
                              : capacityPercent >= 70
                              ? "bg-gradient-to-r from-yellow-500 to-yellow-600"
                              : "bg-gradient-to-r from-green-500 to-green-600"
                          )}
                          style={{ width: `${Math.min(capacityPercent, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={clsx(
              "text-center py-8 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-600"
            )}>
              <FireIcon className={clsx(
                "h-12 w-12 mx-auto mb-3 transition-colors",
                theme === 'dark' ? "text-gray-500" : "text-gray-400"
              )} />
              <p>No classes today</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 